/**
 * @file AppTableToolbar.tsx
 * @description Toolbar component for AppTable with search, settings, and actions.
 * 
 * GOVERNED BY: copilot-instructions.md – Sections 7, 23 (UI Standards)
 */

import { memo, type ReactNode } from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from '../input';
import { Button } from '../button';
import { AppTableSettings } from './AppTableSettings';
import type { AppTableColumn, VisibleColumnsState } from './appTableTypes';

export interface AppTableToolbarProps<TRow> {
  /** Search value */
  searchValue?: string;
  /** Search change handler */
  onSearchChange?: (value: string) => void;
  /** Search placeholder */
  searchPlaceholder?: string;
  /** Whether to show search */
  showSearch?: boolean;
  
  /** Column definitions (for settings) */
  columns?: AppTableColumn<TRow>[];
  /** Visible columns state */
  visibleColumns?: VisibleColumnsState;
  /** Visible columns change handler */
  onVisibleColumnsChange?: (columns: VisibleColumnsState) => void;
  /** Reset column visibility */
  onResetColumns?: () => void;
  /** Unique table key for persistence */
  tableKey?: string;
  /** Whether to show settings gear */
  showSettings?: boolean;
  
  /** Primary action (e.g., "Add New" button) */
  primaryAction?: ReactNode;
  
  /** Additional filter slot */
  filters?: ReactNode;
  
  /** Left side custom content */
  leftContent?: ReactNode;
  
  /** Right side custom content (before settings) */
  rightContent?: ReactNode;
  
  /** Custom settings component (replaces default AppTableSettings) */
  customSettings?: ReactNode;
  
  /** Children (rendered in right side before settings) */
  children?: ReactNode;
  
  /** Additional className */
  className?: string;
}

/**
 * AppTableToolbar - Toolbar for AppTable.
 * 
 * Features:
 * - Search input with clear button
 * - Settings gear with column visibility
 * - Primary action slot
 * - Filter slot
 * - Custom content slots
 */
export const AppTableToolbar = memo(function AppTableToolbar<TRow>({
  searchValue = '',
  onSearchChange,
  searchPlaceholder = 'Search...',
  showSearch = true,
  columns,
  visibleColumns,
  onVisibleColumnsChange,
  onResetColumns,
  tableKey,
  showSettings = true,
  primaryAction,
  filters,
  leftContent,
  rightContent,
  customSettings,
  children,
  className,
}: AppTableToolbarProps<TRow>) {
  const hasSearch = showSearch && onSearchChange;
  const hasSettings = showSettings && columns && visibleColumns && onVisibleColumnsChange && tableKey;

  return (
    <div className={cn('flex flex-col gap-2 px-3 py-2 border-b border-border/50 sm:flex-row sm:items-center sm:justify-between', className)}>
      {/* Left Side */}
      <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0 w-full sm:w-auto">
        {/* Search */}
        {hasSearch && (
          <div className="relative w-full max-w-[280px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              type="text"
              value={searchValue}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onSearchChange(e.target.value)}
              placeholder={searchPlaceholder}
              className="h-8 pl-8 pr-8 text-[12px]"
            />
            {searchValue && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onSearchChange('')}
                className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0 hover:bg-transparent"
              >
                <X className="w-3 h-3 text-muted-foreground hover:text-foreground" />
              </Button>
            )}
          </div>
        )}

        {/* Filters */}
        {filters && (
          <div className="flex items-center gap-2">
            {filters}
          </div>
        )}

        {/* Custom left content */}
        {leftContent}
      </div>

      {/* Right Side */}
      <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto sm:justify-end">
        {/* Custom right content */}
        {rightContent}
        
        {/* Children slot */}
        {children}

        {/* Settings Gear (or custom settings) */}
        {customSettings || (hasSettings && (
          <AppTableSettings
            columns={columns}
            visibleColumns={visibleColumns}
            onVisibleColumnsChange={onVisibleColumnsChange}
            tableKey={tableKey}
            onReset={onResetColumns}
          />
        ))}

        {/* Primary Action */}
        {primaryAction}
      </div>
    </div>
  );
}) as <TRow>(props: AppTableToolbarProps<TRow>) => React.ReactElement;

export default AppTableToolbar;
